import { useState, type ReactNode } from 'react';

import { Button, Card, Field, Input, MonoLabel } from '@/design-system';
import {
    canMoveInvoiceTo,
    formatMoney,
    formatPercentage,
    isInvoiceEditable,
    type Invoice,
    type InvoiceLine,
} from '@/entities/invoice';
import { problemKey } from '@/shared/api';
import { formatDayLong } from '@/shared/lib';

import { browser } from '../browser';
import { billingCopy } from '../copy';
import { describeBillingError } from '../errors';
import {
    useChangeInvoiceStatus,
    useCreateInvoice,
    useInvoice,
    useInvoicePdfLink,
    useSendInvoice,
} from '../hooks';
import { DraftInvoiceEditor } from './DraftInvoiceEditor';

export interface InvoiceDetailProps {
    invoice: Invoice;
    /**
     * Voorbereiden én uitgeven zijn vandaag allebei van de eigenaar
     * (`invoices.manage` en `billing.manage`); wie dat niet is, ziet die knoppen niet.
     */
    canSend: boolean;
    /** Wat er gelukt is, voor onder de tabel: de factuur kan na de actie uit het filter vallen. */
    onDone: (message: string) => void;
}

type Confirming = 'send' | 'paid' | 'credit' | null;

/**
 * De balk onder de facturentabel: de geselecteerde factuur en wat je ermee kunt.
 *
 * Elke knop hoort bij een stap die de API op dit moment toestaat
 * (`InvoiceStatus::allowedTransitions()`); een stap die niet terug kan, vraagt
 * eerst om bevestiging en zegt wat er gebeurt.
 *
 * Het verschil tussen een concept en een verstuurde factuur staat er met zoveel
 * woorden: een concept bewerk je hier helemaal, een verstuurde factuur is een
 * vastgelegd document en blijft zoals hij is. Dat wordt niet stilletjes
 * geblokkeerd maar uitgelegd, met de weg vooruit erbij - crediteren en een
 * nieuwe factuur met dezelfde regels.
 */
export function InvoiceDetail({ invoice, canSend, onDone }: InvoiceDetailProps) {
    const [confirming, setConfirming] = useState<Confirming>(null);
    const [editing, setEditing] = useState(false);
    const [reason, setReason] = useState('');
    const [pdfProblem, setPdfProblem] = useState<string | null>(null);
    const [blockedPdfUrl, setBlockedPdfUrl] = useState<string | null>(null);

    const send = useSendInvoice();
    const changeStatus = useChangeInvoiceStatus();
    const pdfLink = useInvoicePdfLink();
    const copy = useCreateInvoice();

    /* De lijst stuurt geen regels mee; die haalt deze balk er zelf bij. */
    const detail = useInvoice(invoice.uuid);
    const full = detail.data ?? invoice;
    const lines = detail.data?.lines ?? null;

    const total = formatMoney(full.total);
    const editable = isInvoiceEditable(full);
    const busy = send.isPending || changeStatus.isPending;
    const actionError = send.error ?? changeStatus.error;

    function close() {
        setConfirming(null);
        setReason('');
        send.reset();
        changeStatus.reset();
    }

    async function openPdf() {
        setPdfProblem(null);
        setBlockedPdfUrl(null);

        /* Een concept heeft nog geen PDF; dat zeggen we zonder de API te bellen. */
        if (editable) {
            setPdfProblem(billingCopy.pdfDraft);

            return;
        }

        /* Het tabblad opent tijdens de klik; de link komt er daarna in. */
        const tab = browser.openBlankTab();

        try {
            const link = await pdfLink.mutateAsync(invoice.uuid);

            if (tab) {
                browser.navigateTab(tab, link.url);
            } else {
                setBlockedPdfUrl(link.url);
            }
        } catch (error) {
            tab?.close();
            setPdfProblem(
                problemKey(error) === 'invoice-pdf-not-available'
                    ? billingCopy.pdfNotReady
                    : describeBillingError(error),
            );
        }
    }

    function copyToDraft() {
        if (lines === null || full.clientUuid === null) {
            return;
        }

        copy.mutate(
            {
                clientUuid: full.clientUuid,
                orderUuid: full.orderUuid,
                reference: full.reference,
                lines: lines.map((line) => ({
                    description: line.description,
                    quantity: line.quantity,
                    unitPrice: line.unitPrice,
                    vatRate: line.vatRate,
                })),
            },
            { onSuccess: (draft) => onDone(billingCopy.copied(draft.label)) },
        );
    }

    return (
        <div className="border-t border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-[14px]">
            <div className="font-[family-name:var(--font-display)] text-[16px] leading-[1.3] font-semibold text-[var(--text-primary)]">
                {invoice.address ?? billingCopy.noObject}
            </div>
            <div className="mt-1">
                <MonoLabel size={11} variant="value" tone="muted">
                    {[
                        invoice.label,
                        invoice.statusLabel,
                        invoice.clientName,
                        invoice.orderNumber ? billingCopy.keuring(invoice.orderNumber) : null,
                    ]
                        .filter(Boolean)
                        .join(' · ')}
                </MonoLabel>
            </div>

            <dl className="m-0 mt-3 grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-x-4 gap-y-2">
                <Fact label={billingCopy.subtotal} value={formatMoney(full.subtotal)} mono />
                <Fact label={billingCopy.vat} value={formatMoney(full.vatAmount)} mono />
                <Fact label={billingCopy.total} value={total} mono />
                {full.issuedAt ? (
                    <Fact label={billingCopy.issuedAt} value={formatDayLong(full.issuedAt)} />
                ) : null}
                {full.dueAt ? (
                    <Fact label={billingCopy.dueAt} value={formatDayLong(full.dueAt)} />
                ) : null}
                {full.paidAt ? (
                    <Fact label={billingCopy.paidAt} value={formatDayLong(full.paidAt)} />
                ) : null}
                {full.reference ? (
                    <Fact label={billingCopy.reference} value={full.reference} />
                ) : null}
            </dl>

            {lines !== null && !editing ? <Lines lines={lines} /> : null}

            <p className="m-0 mt-2 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                {editable
                    ? billingCopy.withReportDraft
                    : full.hasPdf
                      ? billingCopy.withReportReady(full.label)
                      : billingCopy.withReportRendering}
            </p>

            {!editable ? (
                <Card variant="muted" padding="md" radius={9} className="mt-3" role="note">
                    <div className="text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                        <strong className="font-semibold text-[var(--text-primary)]">
                            {billingCopy.lockedTitle}
                        </strong>{' '}
                        {full.status === 'credited'
                            ? billingCopy.lockedCredited(full.label)
                            : full.status === 'paid'
                              ? billingCopy.lockedPaid(full.label)
                              : billingCopy.locked(full.label)}
                    </div>
                    {lines !== null && full.clientUuid !== null ? (
                        <Button
                            variant="outline"
                            size="sm"
                            icon="add"
                            className="mt-3"
                            disabled={copy.isPending}
                            onClick={copyToDraft}
                        >
                            {copy.isPending ? billingCopy.copying : billingCopy.copyToDraft}
                        </Button>
                    ) : null}
                    {copy.isError ? (
                        <p
                            role="alert"
                            className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--danger)]"
                        >
                            {describeBillingError(copy.error)}
                        </p>
                    ) : null}
                </Card>
            ) : null}

            {editing && lines !== null ? (
                <DraftInvoiceEditor
                    invoice={full}
                    lines={lines}
                    onSaved={(message) => {
                        setEditing(false);
                        onDone(message);
                    }}
                    onCancel={() => setEditing(false)}
                />
            ) : null}

            {confirming ? (
                <Card
                    variant="warn"
                    padding="md"
                    radius={9}
                    className="mt-3"
                    role="group"
                    aria-label={
                        confirming === 'send'
                            ? billingCopy.sendConfirmLabel(invoice.label)
                            : confirming === 'paid'
                              ? billingCopy.paidConfirmLabel(invoice.label)
                              : billingCopy.creditConfirmLabel(invoice.label)
                    }
                >
                    <p className="m-0 text-[13px] leading-[1.5] text-[var(--warn-ink)]">
                        {confirming === 'send'
                            ? billingCopy.sendConfirm(invoice.label, total)
                            : confirming === 'paid'
                              ? billingCopy.paidConfirm(invoice.label, total)
                              : billingCopy.creditConfirm(invoice.label, total)}
                    </p>

                    {confirming === 'credit' ? (
                        <Field
                            label={billingCopy.creditReason}
                            size="sm"
                            className="mt-3"
                            description={billingCopy.creditReasonHelp}
                        >
                            <Input
                                value={reason}
                                maxLength={500}
                                placeholder={billingCopy.creditReasonPlaceholder}
                                onChange={(event) => setReason(event.target.value)}
                            />
                        </Field>
                    ) : null}

                    {actionError ? (
                        <p
                            role="alert"
                            className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--danger)]"
                        >
                            {describeBillingError(actionError)}
                        </p>
                    ) : null}

                    <div className="mt-3 flex flex-wrap gap-2">
                        {confirming === 'send' ? (
                            <Button
                                variant="warn"
                                size="sm"
                                disabled={busy}
                                onClick={() =>
                                    send.mutate(invoice.uuid, {
                                        onSuccess: (sent) => {
                                            close();
                                            onDone(billingCopy.sentAs(sent.label));
                                        },
                                    })
                                }
                            >
                                {billingCopy.sendYes}
                            </Button>
                        ) : (
                            <Button
                                variant="warn"
                                size="sm"
                                disabled={busy || (confirming === 'credit' && reason.trim() === '')}
                                onClick={() =>
                                    changeStatus.mutate(
                                        {
                                            invoiceUuid: invoice.uuid,
                                            status: confirming === 'paid' ? 'paid' : 'credited',
                                            reason:
                                                confirming === 'credit' ? reason.trim() : undefined,
                                        },
                                        {
                                            onSuccess: (changed) => {
                                                close();
                                                onDone(
                                                    confirming === 'paid'
                                                        ? billingCopy.markedPaid(changed.label)
                                                        : billingCopy.credited(changed.label),
                                                );
                                            },
                                        },
                                    )
                                }
                            >
                                {confirming === 'paid'
                                    ? billingCopy.paidYes
                                    : billingCopy.creditYes}
                            </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={close}>
                            {billingCopy.cancel}
                        </Button>
                    </div>
                </Card>
            ) : editing ? null : (
                <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        icon="download"
                        disabled={pdfLink.isPending}
                        onClick={() => void openPdf()}
                    >
                        {pdfLink.isPending ? billingCopy.openingPdf : billingCopy.openPdf}
                    </Button>
                    {canSend && editable ? (
                        <Button
                            variant="outline"
                            size="sm"
                            icon="edit"
                            disabled={lines === null}
                            onClick={() => {
                                setEditing(true);
                                setPdfProblem(null);
                            }}
                        >
                            {billingCopy.editDraft}
                        </Button>
                    ) : null}
                    {canSend && canMoveInvoiceTo(full, 'sent') ? (
                        <Button
                            variant="outline"
                            size="sm"
                            icon="send"
                            onClick={() => setConfirming('send')}
                        >
                            {billingCopy.send}
                        </Button>
                    ) : null}
                    {canMoveInvoiceTo(full, 'paid') ? (
                        <Button variant="outline" size="sm" onClick={() => setConfirming('paid')}>
                            {billingCopy.markPaid}
                        </Button>
                    ) : null}
                    {canMoveInvoiceTo(full, 'credited') ? (
                        <Button variant="outline" size="sm" onClick={() => setConfirming('credit')}>
                            {billingCopy.credit}
                        </Button>
                    ) : null}
                </div>
            )}

            {pdfProblem ? (
                <p
                    role="alert"
                    className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--warn-ink)]"
                >
                    {pdfProblem}
                </p>
            ) : null}

            {blockedPdfUrl ? (
                <p
                    role="status"
                    className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--text-secondary)]"
                >
                    {billingCopy.pdfBlocked}{' '}
                    <a
                        href={blockedPdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[var(--accent)] underline-offset-2 hover:underline"
                    >
                        {billingCopy.openPdf}
                    </a>
                </p>
            ) : null}
        </div>
    );
}

/**
 * De regels zoals ze op de factuur komen te staan.
 *
 * Geen tabel maar een lijst: dit staat al binnen de facturentabel, en het aantal,
 * de stukprijs en het btw-tarief horen bij hun omschrijving - niet in een eigen
 * kolom die op een tablet toch wegvalt.
 */
function Lines({ lines }: { lines: readonly InvoiceLine[] }) {
    return (
        <div className="mt-3">
            <MonoLabel size={10}>{billingCopy.linesTitle}</MonoLabel>
            <ul className="m-0 mt-1 list-none p-0">
                {lines.map((line, index) => (
                    <li
                        key={`${line.sortOrder}-${index}`}
                        className="border-t border-[var(--border-subtle-2)] py-[7px]"
                    >
                        <div className="flex items-baseline justify-between gap-3">
                            <span className="min-w-0 flex-1 text-[13px] leading-[1.4] text-[var(--text-primary)]">
                                {line.description}
                            </span>
                            <MonoLabel size={11} variant="value" tone="default">
                                {formatMoney(line.total)}
                            </MonoLabel>
                        </div>
                        <span className="text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                            {`${formatQuantity(line.quantity)} × ${formatMoney(line.unitPrice)} · btw ${formatPercentage(line.vatRate)}`}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/** `"1.00"` -> `1`, `"1.50"` -> `1,5`. */
function formatQuantity(quantity: string): string {
    const [whole = '0', fraction = ''] = quantity.split('.');
    const decimals = fraction.replace(/0+$/, '');

    return decimals === '' ? whole : `${whole},${decimals}`;
}

function Fact({ label, value, mono = false }: { label: ReactNode; value: string; mono?: boolean }) {
    return (
        <div className="min-w-0">
            <dt className="text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">{label}</dt>
            <dd
                className={
                    mono
                        ? 'm-0 font-[family-name:var(--font-mono)] text-[12px] leading-[1.5] text-[var(--text-primary)]'
                        : 'm-0 text-[13px] leading-[1.5] text-[var(--text-primary)]'
                }
            >
                {value}
            </dd>
        </div>
    );
}
